import React from 'react';
import { FeaturePageLayout } from '@/components/shared/FeaturePageLayout';
import { HelpCircle } from 'lucide-react';

export default function FaqAutomationPage() {
  return (
    <FeaturePageLayout
      icon={HelpCircle}
      title="FAQ Automation"
      badge="Automation Feature"
      description="Handle repetitive customer questions instantly and automatically. Your AI agent resolves the most common queries in seconds, freeing your team to focus on complex, high-value interactions."
      capabilities={[
        {
          title: 'Instant Answers',
          description: 'Common questions are resolved in under 2 seconds. Customers get accurate answers without waiting for a human.',
        },
        {
          title: 'Smart Question Matching',
          description: 'The AI understands variations of the same question. "What time do you open?" and "Working hours?" get the same answer.',
        },
        {
          title: 'Category Organization',
          description: 'Organize FAQs into categories like Shipping, Returns, Pricing, and General. The AI navigates these intelligently.',
        },
        {
          title: 'Auto-Learning',
          description: 'When new questions emerge frequently, the system flags them so you can add authoritative answers to the FAQ base.',
        },
        {
          title: 'Rich Responses',
          description: 'Send text, images, links, or formatted messages in FAQ responses. Support product images, maps, and documents.',
        },
        {
          title: 'Seamless Escalation',
          description: 'If a question goes beyond FAQ scope, the AI smoothly transitions to a human agent or collects details for callback.',
        },
      ]}
      howItWorks={[
        {
          title: 'Upload Your FAQ Content',
          description: 'Add your frequently asked questions and answers through the dashboard or upload a structured FAQ document.',
        },
        {
          title: 'AI Learns Your Answers',
          description: 'The AI indexes your FAQ content, understands context, and prepares to match incoming questions to the right answers.',
        },
        {
          title: 'Automatic Resolution',
          description: 'When a customer asks a question that matches your FAQs, the AI responds instantly with the correct answer.',
        },
        {
          title: 'Review & Improve',
          description: 'Monitor which FAQs are most asked, which need better answers, and which new questions you should add.',
        },
      ]}
      useCases={[
        { emoji: '🛒', title: 'Order Tracking', description: 'Answer "Where is my order?" instantly by connecting order status information to your AI agent.' },
        { emoji: '🔄', title: 'Return Policy', description: 'Handle the most common question — return and exchange policies — without human intervention.' },
        { emoji: '💳', title: 'Payment Methods', description: 'Automatically inform customers about accepted payment methods, UPI IDs, and bank details.' },
        { emoji: '📦', title: 'Shipping Info', description: 'Respond to delivery timelines, shipping charges, and pin code serviceability questions automatically.' },
        { emoji: '⏰', title: 'Business Hours', description: 'Answer operating hours, holiday schedules, and contact availability questions round the clock.' },
        { emoji: '📋', title: 'Service Details', description: 'Share pricing, packages, and service descriptions for any business type automatically.' },
      ]}
      ctaText="Automate your most common questions"
    />
  );
}
